import {
  ActionBanner,
  ActionRowCard,
  Dialog,
  DialogHeader,
  DialogIcon,
  IconButton,
  PrimaryButton,
  DateField,
  MoonFilterChip,
  MoonFormField,
  MoonNumericField,
  PartialDateField,
  TextActionButton,
  colors,
  TextField,
} from "@louise/moon-ui";
/**
 * Where a patient says where her pregnancy stands.
 *
 * Two steps, following the end-cycle dialog rather than inventing a shape: pick
 * one of three, then say as much or as little as you want about it. Choosing
 * advances, so the whole list of loss kinds is never on screen beside the choice
 * that produced it, which is what made the single-page version overflow. The
 * chevron in the corner goes back to the three.
 *
 * All the detail is optional. Save is live the moment step two opens, so someone
 * telling the app her pregnancy has ended can say so and stop. What she is asked
 * first, when it ended, is to say anything she would like in her own words, when
 * it happened included. A birth asks the day the baby was born and leaves room
 * for her own words too. What a clinic tells you, how far along and which kind of
 * loss, waits behind "Add details": "missed miscarriage" and "anembryonic
 * pregnancy" are words a clinic says to you, so "I'm not sure" is the standing
 * answer and the record then says a pregnancy ended and claims nothing more.
 * Ectopic is on the list because it matters medically, not because anyone should
 * have to identify it.
 *
 * There is no confirmation step. She does not say it twice.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { View, ScrollView, useWindowDimensions } from "react-native";
import {
  Heart,
  HeartCrack,
  Baby,
  Calendar as CalendarIcon,
  ChevronLeft,
  Plus,
  StickyNote,
} from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type {
  PatientPregnancyLossType,
  PatientPregnancyStands,
} from "@src/packages/homepage/pregnancy";
import type { PregnancyStatus } from "@src/packages/cycles/presenters/pregnancyStatusUpdate";
import { monthYearToStartDate } from "@src/packages/cycles/presenters/monthYearInput";
import type {
  CyclePregnancyStatusRecord,
  PatientPregnancyDatePrecision,
} from "@src/packages/homepage/pregnancy/useCyclePregnancyStatus";
import endingIllustration from "../../../../assets/images/end-treatment-cycle-illustration.png";

const NS = "care.update_pregnancy";

/**
 * The three things that can be true, in the order they are offered.
 *
 * Typed against the API's own vocabulary rather than merely spelled the same:
 * a state added to the contract that this list does not offer is a state a
 * patient cannot reach, so it has to break the build here.
 */
const STANDS = [
  "continuing",
  "ended",
  "birth",
] as const satisfies readonly PatientPregnancyStands[];
type Stands = (typeof STANDS)[number];
const STAND_ICONS: Record<PatientPregnancyStands, typeof Heart> = {
  continuing: Heart,
  ended: HeartCrack,
  birth: Baby,
};

/**
 * The kinds of loss, ordered by how likely a patient is to have been given the
 * words rather than by clinical taxonomy.
 *
 * `recurrent_miscarriage` is in the API's vocabulary but not offered: it names
 * a history of losses, a diagnosis, not what happened in this one pregnancy. A
 * record that already holds it still shows it, so saving never changes it.
 */
const LOSS_KINDS: PatientPregnancyLossType[] = [
  "complete_miscarriage",
  "incomplete_miscarriage",
  "missed_miscarriage",
  "chemical_pregnancy",
  "ectopic_pregnancy",
  "anembryonic_pregnancy",
  "still_birth",
];

/** Option id for "I'm not sure", which records no loss kind at all. */
const LOSS_KIND_NOT_SURE = "unknown";

export default function UpdatePregnancyModal({
  visible,
  onClose,
  onSave,
  isSaving = false,
  readReady = true,
  current = null,
  currentDetails = null,
  error,
}: {
  visible: boolean;
  onClose: () => void;
  /**
   * `details` is what she wrote in her own words about the pregnancy ending or
   * the birth, or null for an answer that asks for none.
   */
  onSave: (status: PregnancyStatus, details: string | null) => void;
  isSaving?: boolean;
  readReady?: boolean;
  current?: CyclePregnancyStatusRecord | null;
  /** What she last wrote about this pregnancy, if anything. */
  currentDetails?: string | null;
  error?: string | null;
}) {
  const t = useTranslations();
  const { height } = useWindowDimensions();
  // The same bounds the beta dialog uses, so neither can outgrow a short window.
  const dialogMaxHeight = Math.min(760, Math.max(480, height * 0.85));
  const [stands, setStands] = useState<Stands | null>(null);
  const [weeks, setWeeks] = useState<number | null>(null);
  const [lossKind, setLossKind] = useState<PatientPregnancyLossType | null>(
    null,
  );
  const [details, setDetails] = useState("");
  /**
   * Whether how far along and the kind of loss are on screen. They wait behind
   * "Add details" until she asks for them, unless she has given them before.
   */
  const [clinicDetailsShown, setClinicDetailsShown] = useState(false);
  const [births, setBirths] = useState<number | null>(null);
  /**
   * The day the baby was born.
   *
   * Optional like everything else on step two, and empty by default rather than
   * pre-filled with today: an event date the app guessed is worse than none.
   */
  const [eventDate, setEventDate] = useState("");
  /**
   * A birth recorded earlier with only a month or a year, as she would type it
   * ("MM/YYYY" or "YYYY"). It is shown in the month or year field in place of
   * the day, so she sees what is on her record and can change or clear it.
   */
  const [partialDate, setPartialDate] = useState<{
    precision: PartialPrecision;
    draft: string;
  } | null>(null);

  const reset = useCallback(() => {
    setStands(null);
    setWeeks(current?.gestationalWeek ?? null);
    setLossKind(current?.lossType ?? null);
    setDetails(
      current?.status === "ended" || current?.status === "birth"
        ? (currentDetails ?? "")
        : "",
    );
    setClinicDetailsShown(
      current?.status === "ended" &&
        (current.gestationalWeek != null || current.lossType != null),
    );
    setBirths(current?.liveBirthCount ?? null);
    const precision = current?.eventDatePrecision ?? "day";
    const date = current?.eventDate ?? "";
    setEventDate(precision === "day" ? date : "");
    setPartialDate(
      precision !== "day" && date
        ? { precision, draft: partialDateToDraft(date, precision) }
        : null,
    );
  }, [current, currentDetails]);

  /**
   * Clear it whenever it closes, not only when she dismisses it.
   *
   * Both callers close on a successful save by hiding the dialog directly, which
   * never reaches `handleClose`, so the answers survived. Two weeks after saving
   * "still pregnant, 8 weeks" she reopened it already on step two, pre-answered,
   * with the three choices out of sight.
   */
  const initialized = useRef(false);
  useEffect(() => {
    if (!visible) {
      initialized.current = false;
      reset();
    } else if (readReady && !initialized.current) {
      reset();
      initialized.current = true;
    }
  }, [visible, readReady, reset]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    reset();
    onClose();
  }, [isSaving, onClose, reset]);

  // The month or year she sees, as the API stores it, or null when she cleared
  // it or has not finished typing it.
  const partialEventDate =
    stands === "birth" && partialDate ? draftToPartialDate(partialDate) : null;
  // Half a month or year is not a date: Save waits for a whole one or none.
  const partialDateIncomplete = Boolean(
    stands === "birth" && partialDate?.draft && !partialEventDate,
  );

  const handleSave = useCallback(() => {
    if (!stands || isSaving || !readReady || partialDateIncomplete) return;
    if (stands === "continuing") {
      onSave({ stands: "continuing", weekCount: weeks }, null);
      return;
    }
    if (stands === "ended") {
      // This sheet asks for no date of its own: when it happened is hers to
      // say in her words. A date an earlier answer gave is carried through
      // rather than dropped by a correction that never mentioned it.
      const earlier = current?.status === "ended" ? current : null;
      onSave(
        {
          stands: "ended",
          kind: lossKind,
          weekCount: weeks,
          eventDate: earlier?.eventDate ?? null,
          eventDatePrecision: earlier?.eventDatePrecision ?? undefined,
        },
        details,
      );
      return;
    }
    onSave(
      {
        stands: "birth",
        // More than one baby is the only thing the count can mean here.
        kind: births
          ? births > 1
            ? "multiple_live_birth"
            : "live_birth"
          : current?.status === "birth"
            ? (current.birthType ?? "live_birth")
            : "live_birth",
        liveBirthCount: births,
        ...(eventDate
          ? { eventDate, eventDatePrecision: "day" as const }
          : (partialEventDate ?? {
              eventDate: null,
              eventDatePrecision: "day" as const,
            })),
      },
      details,
    );
  }, [
    births,
    current,
    details,
    eventDate,
    partialEventDate,
    partialDateIncomplete,
    isSaving,
    readReady,
    lossKind,
    onSave,
    stands,
    weeks,
  ]);

  // A kind of loss already on her record stays offered, so saving shows it and
  // never quietly changes it, even when it is one no longer offered.
  const lossKinds =
    current?.lossType && !LOSS_KINDS.includes(current.lossType)
      ? [...LOSS_KINDS, current.lossType]
      : LOSS_KINDS;

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
      testID="update-pregnancy-modal"
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 20,
        paddingHorizontal: 22,
        paddingBottom: 18,
        maxHeight: dialogMaxHeight,
      }}
    >
      {/* Back to the three answers. It clears what the last answer collected:
          a week typed for "still pregnant" is not an answer about a birth. */}
      {stands ? (
        <IconButton
          accessibilityLabel={t(`${NS}.choose_a_different_answer`)}
          action="back"
          onPress={reset}
          disabled={isSaving}
          hitSlop={8}
          size="sm"
          pressedOpacity={1}
          testID="update-pregnancy-back"
          style={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}
        >
          <ChevronLeft size={20} color={colors.textLight} strokeWidth={2} />
        </IconButton>
      ) : null}
      <View style={{ flex: 1, minHeight: 0 }}>
        <DialogIcon
          source={endingIllustration}
          width={56}
          height={62}
          style={{ marginBottom: 14 }}
        />
        <DialogHeader
          align="center"
          title={
            stands ? t(`${NS}.stands_${stands}`) : t(`${NS}.where_things_stand`)
          }
          subtitle={
            stands
              ? t(`${NS}.add_anything_you_want_to`)
              : t(`${NS}.tell_us_only_what_you_want_to`)
          }
        />

        <ScrollView
          contentContainerStyle={{ paddingTop: 14, paddingBottom: 4 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {!stands ? (
            <View style={{ gap: 10 }}>
              {STANDS.map((option) => {
                const Icon = STAND_ICONS[option];
                return (
                  <ActionRowCard
                    key={option}
                    leading={<Icon size={18} color={colors.textMid} />}
                    title={t(`${NS}.stands_${option}`)}
                    onPress={() => {
                      if (option !== current?.status) {
                        setWeeks(null);
                        setLossKind(null);
                        setDetails("");
                        setClinicDetailsShown(false);
                        setBirths(null);
                        setEventDate("");
                        setPartialDate(null);
                      }
                      setStands(option);
                    }}
                    accessibilityLabel={t(`${NS}.stands_${option}`)}
                  />
                );
              })}
            </View>
          ) : (
            <View style={{ gap: 18 }}>
              {stands === "birth" && partialDate ? (
                <PartialDateField
                  label={t(`${NS}.when_was_the_baby_born`)}
                  labelIcon={CalendarIcon}
                  hint={t(`${NS}.only_if_you_want_to_give_a_date`)}
                  precision={partialDate.precision}
                  value={partialDate.draft}
                  onChange={(draft) =>
                    setPartialDate({ precision: partialDate.precision, draft })
                  }
                  disabled={isSaving}
                  testID="update-pregnancy-event-date"
                />
              ) : stands === "birth" ? (
                <DateField
                  label={t(`${NS}.when_was_the_baby_born`)}
                  labelIcon={CalendarIcon}
                  hint={t(`${NS}.only_if_you_want_to_give_a_date`)}
                  value={eventDate}
                  onChange={setEventDate}
                  disabled={isSaving}
                  testID="update-pregnancy-event-date"
                />
              ) : null}

              {stands === "continuing" ? (
                <MoonNumericField
                  label={t(`${NS}.how_many_weeks_if_you_know`)}
                  value={weeks}
                  onChange={setWeeks}
                  placeholder={t(`${NS}.weeks_placeholder`)}
                  min={0}
                  // A pregnancy does not run past ~45 weeks, so a stray digit is a
                  // typo rather than a record.
                  max={45}
                  disabled={isSaving}
                  testID="update-pregnancy-weeks"
                />
              ) : null}

              {stands === "ended" ? (
                <View style={{ gap: 16 }}>
                  <PregnancyDetailsField
                    value={details}
                    onChange={setDetails}
                    placeholder={t(`${NS}.when_it_happened_or_anything_else`)}
                    disabled={isSaving}
                  />

                  {clinicDetailsShown ? (
                    <View style={{ gap: 16 }}>
                      <MoonNumericField
                        label={t(`${NS}.how_far_along`)}
                        value={weeks}
                        onChange={setWeeks}
                        placeholder="0"
                        suffix={t(`${NS}.weeks`)}
                        min={0}
                        max={45}
                        disabled={isSaving}
                        testID="update-pregnancy-weeks"
                      />
                      <MoonFormField label={t(`${NS}.kind_of_loss`)}>
                        <View
                          style={{
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: 6,
                          }}
                        >
                          {[
                            // The standing answer, not a placeholder.
                            {
                              id: LOSS_KIND_NOT_SURE,
                              label: t(`${NS}.i_am_not_sure`),
                            },
                            ...lossKinds.map((kind) => ({
                              id: kind,
                              label: t(`${NS}.loss_${kind}`),
                            })),
                          ].map((option) => (
                            <MoonFilterChip
                              key={option.id}
                              label={option.label}
                              size="sm"
                              selected={
                                (lossKind ?? LOSS_KIND_NOT_SURE) === option.id
                              }
                              onPress={() =>
                                setLossKind(
                                  lossKinds.find(
                                    (kind) => kind === option.id,
                                  ) ?? null,
                                )
                              }
                              disabled={isSaving}
                              action="pregnancy_detail_chip"
                              testID={`update-pregnancy-loss-${option.id}`}
                            />
                          ))}
                        </View>
                      </MoonFormField>
                    </View>
                  ) : (
                    <TextActionButton
                      label={t(`${NS}.add_details`)}
                      action="pregnancy_add_details"
                      size="sm"
                      icon={Plus}
                      onPress={() => setClinicDetailsShown(true)}
                      disabled={isSaving}
                      testID="update-pregnancy-add-details"
                      style={{ alignSelf: "flex-start", marginLeft: -8 }}
                    />
                  )}
                </View>
              ) : null}

              {stands === "birth" ? (
                <>
                  <MoonNumericField
                    label={t(`${NS}.how_many_babies_if_you_want_to_say`)}
                    value={births}
                    onChange={setBirths}
                    placeholder={t(`${NS}.babies_placeholder`)}
                    min={1}
                    max={10}
                    disabled={isSaving}
                    testID="update-pregnancy-births"
                  />
                  <PregnancyDetailsField
                    value={details}
                    onChange={setDetails}
                    placeholder={t(`${NS}.anything_about_the_birth`)}
                    disabled={isSaving}
                  />
                </>
              ) : null}
            </View>
          )}

          {error ? (
            <ActionBanner
              variant="important"
              message={error}
              style={{ marginTop: 14 }}
            />
          ) : null}
        </ScrollView>

        {stands ? (
          <View style={{ marginTop: 16 }}>
            <PrimaryButton
              label={t(`${NS}.save`)}
              onPress={handleSave}
              loading={isSaving}
              disabled={isSaving || !readReady || partialDateIncomplete}
              fullWidth
              testID="update-pregnancy-save"
            />
          </View>
        ) : null}
      </View>
    </Dialog>
  );
}

type PartialPrecision = Exclude<PatientPregnancyDatePrecision, "day">;

/** A month- or year-precise date from the API ("YYYY-MM-DD"), as she types it. */
function partialDateToDraft(date: string, precision: PartialPrecision): string {
  return precision === "month"
    ? `${date.slice(5, 7)}/${date.slice(0, 4)}`
    : date.slice(0, 4);
}

/** What she typed as the API stores it, or null unless it is a whole one. */
function draftToPartialDate({
  precision,
  draft,
}: {
  precision: PartialPrecision;
  draft: string;
}): { eventDate: string; eventDatePrecision: PartialPrecision } | null {
  if (precision === "month") {
    const eventDate = monthYearToStartDate(draft);
    return eventDate ? { eventDate, eventDatePrecision: "month" } : null;
  }
  const year = /^\d{4}$/.test(draft) ? Number(draft) : NaN;
  return year >= 1900 && year <= new Date().getFullYear()
    ? { eventDate: `${draft}-01-01`, eventDatePrecision: "year" }
    : null;
}

/** Her own words about the pregnancy: how it ended, or about the birth. */
function PregnancyDetailsField({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  disabled: boolean;
}) {
  const t = useTranslations();
  return (
    <TextField
      label={t(`${NS}.any_details_you_would_like_to_add`)}
      labelIcon={StickyNote}
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      accessibilityLabel={t(`${NS}.any_details_you_would_like_to_add`)}
      editable={!disabled}
      testID="update-pregnancy-details"
      notes
    />
  );
}
